import React, { useState, useEffect } from 'react';
import { Signal } from '@catalyst-iq/shared';
import { SignalsTable } from './signalsTable';
import { api } from '../services/api';

type FlexibleSignal = Signal & {
  change_pct?: number;
  changePct?: number;
  rel_volume?: number;
  relVolume?: number;
  label_reason?: string;
};

const normalizeSignal = (sig: any): FlexibleSignal => {
  const change = sig.changePct ?? sig.change_pct ?? 0;
  const relVol = sig.relVolume ?? sig.rel_volume ?? 0;
  const reason = sig.labelReason ?? sig.label_reason ?? '';

  return {
    ...sig,
    changePct: change,
    change_pct: change,
    relVolume: relVol,
    rel_volume: relVol,
    labelReason: reason,
    label_reason: reason,
    label: sig.label ?? 'none',
  };
};

interface TopGainersSectionProps {
  onCountChange?: (count: number) => void;
  /** Page-level scan — lights this table even when its own fetch isn't running */
  pageScanning?: boolean;
  scanPulse?: number;
  onScanningChange?: (scanning: boolean) => void;
}

const SCAN_INTERVAL_MS = 8000;
const SCAN_HOLD_MS = 1200;

export const TopGainersSection: React.FC<TopGainersSectionProps> = ({
  onCountChange,
  pageScanning = false,
  scanPulse = 0,
  onScanningChange,
}) => {
  const [gainers, setGainers] = useState<FlexibleSignal[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const GAIN_THRESHOLD = 5.0;

  useEffect(() => {
    onScanningChange?.(isScanning);
  }, [isScanning, onScanningChange]);

  useEffect(() => {
    let isFirstFetch = true;
    let scanHoldId: ReturnType<typeof setTimeout>;
    let isMounted = true;

    const fetchSignals = async () => {
      if (isFirstFetch) setIsLoading(true);
      const scanStartedAt = Date.now();
      setIsScanning(true);
      try {
        const data = await api.getSignals({ sort: 'change_pct_desc', limit: 500 });
        if (!isMounted) return;
        const rawSignals = data.signals || [];

        const processedSignals = rawSignals
          .map(normalizeSignal)
          .filter((sig: FlexibleSignal) => (sig.changePct ?? 0) >= GAIN_THRESHOLD)
          .sort((a: FlexibleSignal, b: FlexibleSignal) => (b.changePct ?? 0) - (a.changePct ?? 0));
        setGainers(processedSignals);

        if (onCountChange) {
          onCountChange(processedSignals.length);
        }
      } catch (error) {
        console.error('Error fetching gainers:', error);
        if (isFirstFetch && isMounted) setGainers([]);
      } finally {
        if (!isMounted) return;
        setIsLoading(false);
        isFirstFetch = false;
        const holdMs = Math.max(0, SCAN_HOLD_MS - (Date.now() - scanStartedAt));
        clearTimeout(scanHoldId);
        scanHoldId = setTimeout(() => {
          if (isMounted) setIsScanning(false);
        }, holdMs);
      }
    };

    fetchSignals();
    const intervalId = setInterval(fetchSignals, SCAN_INTERVAL_MS);
    return () => {
      isMounted = false;
      clearInterval(intervalId);
      clearTimeout(scanHoldId);
    };
    // onCountChange is intentionally omitted — parent passes an inline callback
  }, [GAIN_THRESHOLD]);

  const gainersIcon = (
    <svg className="w-3.5 h-3.5 text-emerald-500 dark:text-[#10d98e]" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd"/>
    </svg>
  );

  return (
    <SignalsTable
      title="Top Gainers"
      subtitle={`Tickers up ≥ ${GAIN_THRESHOLD}% · ${gainers.length} active signals`}
      iconNode={gainersIcon}
      topStripGradient="from-[#10d98e] via-emerald-500 to-teal-500"
      headerBgClass="bg-gradient-to-r from-emerald-50/60 via-teal-50/30 to-white dark:from-[#10d98e]/5 dark:via-[#0f1218] dark:to-[#080c14]"
      emptyMessage="No major gainers right now"
      signals={gainers as any}
      isLoading={isLoading}
      isScanning={isScanning || pageScanning}
      scanPulse={scanPulse}
    />
  );
};
